import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Autocomplete,
    MenuItem,
    Button,
    Stack,
    CircularProgress,
    Typography,
    Card,
    CardContent,
    InputAdornment,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PersonIcon from "@mui/icons-material/Person";
import PlaceIcon from "@mui/icons-material/Place";
import NotesIcon from "@mui/icons-material/Notes";

import ClientesService from "../../../../services/clientes.service";
import { RECOGIDA_ESTADO, TIPO_CLIENTE, TIPO_CLIENTE_LABELS, CLIENTE_CONTADO_CODIGO } from "../../constants";

import SeccionDireccion from "./SeccionDireccion";
import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX } from "../../../../components/ui/buttonStyles";

const DEBOUNCE_MS = 300;
const BUSQUEDA_MIN_CARACTERES = 2;
// Límites de la BD (ver tabla en api/docs/api/recogidas.md): pasarse provoca un 400
const MAX_OBSERVACIONES = 200;
const MAX_NUMERO_TICKET = 25;
const MAX_DIRECCION_NOMBRE = 50;
const MAX_DIRECCION = 80;
const MAX_POBLACION = 45;
const MAX_PROVINCIA = 45;

const SX_TIPO_CLIENTE = {
    "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 2 },
    "& .Mui-selected": {
        color: "#cc071e !important",
        borderColor: "#cc071e !important",
    },
};

/**
 * Formulario para crear/editar recogidas
 * @param {Object} recogida - Recogida a editar (null para creación)
 * @param {boolean} cargando - Muestra el loader dentro de la Card, conservando la cabecera
 * @param {string} error - Mensaje de error a mostrar en lugar del formulario
 * @param {boolean} edicion - Fuerza el modo edición mientras `recogida` aún no ha cargado
 */
export default function RecogidaForm({ recogida = null, onSave, onCancel, cargando = false, error = null, edicion }) {
    // Al cargar una edición todavía no hay `recogida`, así que la página puede indicarlo
    // con `edicion` para que la cabecera no diga "Nueva recogida"
    const esEdicion = edicion ?? Boolean(recogida);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    // Estados del formulario
    const [numeroTicket, setNumeroTicket] = useState(recogida?.numeroTicket || "");
    const [observaciones, setObservaciones] = useState(recogida?.observaciones || "");
    const [guardando, setGuardando] = useState(false);

    // Cliente
    const [clienteQuery, setClienteQuery] = useState("");
    const [clienteSeleccionado, setClienteSeleccionado] = useState(
        recogida ? { codigo: recogida.clienteCodigo, nombre: recogida.clienteNombre } : null
    );
    const [clientesOpciones, setClientesOpciones] = useState([]);
    const [loadingClientes, setLoadingClientes] = useState(false);

    // Direcciones
    const [direccionSeleccionada, setDireccionSeleccionada] = useState(
        recogida && recogida.direccionLinea ? {
            linea: parseInt(recogida.direccionLinea, 10), // Convertir a número
            ...recogida.direccion
        } : null
    );
    const [direccionesOpciones, setDireccionesOpciones] = useState([]);
    const [loadingDirecciones, setLoadingDirecciones] = useState(false);

    // De quién se recoge: un cliente de la ficha o un cliente final sin ficha.
    // Una recogida guardada sin `direccionLinea` es de cliente final.
    const [tipoCliente, setTipoCliente] = useState(
        recogida && !recogida.direccionLinea ? TIPO_CLIENTE.CLIENTE_FINAL : TIPO_CLIENTE.CLIENTE
    );
    const esClienteFinal = tipoCliente === TIPO_CLIENTE.CLIENTE_FINAL;

    // La dirección del cliente se ha retocado a mano: se envía suelta
    // (direccionLinea = null) para no tocar la ficha del cliente
    const [direccionModificada, setDireccionModificada] = useState(false);

    // Campos individuales de la dirección
    const [nombreDireccion, setNombreDireccion] = useState("");
    const [direccion, setDireccion] = useState("");
    const [codigoPostal, setCodigoPostal] = useState("");
    const [poblacion, setPoblacion] = useState("");
    const [provincia, setProvincia] = useState("");

    // Cargar dirección de edición (personalizada o existente)
    useEffect(() => {
        if (recogida && recogida.direccion) {
            // La API ahora devuelve direccion como objeto con todos los campos
            setNombreDireccion(recogida.direccion.nombre || '');
            setDireccion(recogida.direccion.direccion || '');
            setCodigoPostal(recogida.direccion.codigoPostal || '');
            setPoblacion(recogida.direccion.poblacion || '');
            setProvincia(recogida.direccion.provincia || '');
        }
    }, [recogida]);

    // Volcar la dirección elegida en el dropdown a los campos individuales
    useEffect(() => {
        // Si ya se ha retocado a mano, no se pisa lo escrito
        if (esClienteFinal || !direccionSeleccionada || direccionModificada) {
            return;
        }

        // DECISION: el nombre de contacto es obligatorio para la API, pero muchas
        // direcciones del cliente llegan sin nombre. Se cae al nombre de la recogida
        // (en edición) y, si tampoco, al del cliente, en vez de dejarlo vacío.
        const nombreGuardado = esEdicion ? recogida?.direccion?.nombre : null;
        setNombreDireccion(
            direccionSeleccionada.nombre || nombreGuardado || clienteSeleccionado?.nombre || ''
        );

        setDireccion(direccionSeleccionada.direccion || '');
        setCodigoPostal(direccionSeleccionada.codigoPostal || '');
        setPoblacion(direccionSeleccionada.poblacion || '');
        setProvincia(direccionSeleccionada.provincia || '');
    }, [direccionSeleccionada, direccionModificada, esClienteFinal, esEdicion, recogida, clienteSeleccionado]);

    // Errores de validación
    const [errores, setErrores] = useState({});

    // Búsqueda de clientes con debounce
    useEffect(() => {
        let alive = true;

        const buscarClientes = async () => {
            if (!clienteQuery || clienteQuery.trim().length < BUSQUEDA_MIN_CARACTERES) {
                setClientesOpciones([]);
                return;
            }

            setLoadingClientes(true);
            try {
                const resultados = await ClientesService.search(clienteQuery.trim());
                if (alive) setClientesOpciones(resultados ?? []);
            } catch (error) {
                console.error('Error al buscar clientes:', error);
                if (alive) setClientesOpciones([]);
            } finally {
                if (alive) setLoadingClientes(false);
            }
        };

        const temporizador = setTimeout(buscarClientes, DEBOUNCE_MS);

        return () => {
            alive = false;
            clearTimeout(temporizador);
        };
    }, [clienteQuery]);

    // Cargar direcciones cuando se selecciona un cliente
    useEffect(() => {
        let alive = true;

        const cargarDirecciones = async () => {
            if (!clienteSeleccionado) {
                setDireccionesOpciones([]);
                if (!esClienteFinal) {
                    setDireccionSeleccionada(null);
                }
                return;
            }

            setLoadingDirecciones(true);
            try {
                const direcciones = await ClientesService.getDirecciones(clienteSeleccionado.codigo);
                if (alive) {
                    setDireccionesOpciones(direcciones ?? []);

                    // En edición con dirección existente, buscar y establecer la dirección completa
                    if (esEdicion && recogida && recogida.direccionLinea && !esClienteFinal) {
                        const lineaBuscada = parseInt(recogida.direccionLinea, 10);
                        const direccionEdicion = direcciones.find(d => parseInt(d.linea, 10) === lineaBuscada);
                        if (direccionEdicion) {
                            setDireccionSeleccionada(direccionEdicion);
                        }
                    }
                    // Auto-seleccionar si solo hay una dirección y NO es de cliente final
                    else if (direcciones.length === 1 && !esEdicion && !esClienteFinal) {
                        setDireccionSeleccionada(direcciones[0]);
                    }
                }
            } catch (error) {
                console.error('Error al cargar direcciones:', error);
                if (alive) setDireccionesOpciones([]);
            } finally {
                if (alive) setLoadingDirecciones(false);
            }
        };

        cargarDirecciones();

        return () => {
            alive = false;
        };
    }, [clienteSeleccionado, esEdicion, esClienteFinal]);

    // Validaciones
    const validar = () => {
        const nuevosErrores = {};

        if (!numeroTicket.trim()) {
            nuevosErrores.numeroTicket = "El número de ticket es requerido";
        }

        // En cliente final no hay buscador: la recogida va contra el cliente contado
        if (!esClienteFinal && !clienteSeleccionado) {
            nuevosErrores.cliente = "El cliente es requerido";
        }

        // Validar nombre de contacto (siempre requerido según API)
        if (!nombreDireccion.trim()) {
            nuevosErrores.nombreDireccion = "El nombre de contacto es requerido";
        }

        // En cliente final no hay dropdown: la dirección se escribe entera a mano
        if (!esClienteFinal && !direccionSeleccionada) {
            nuevosErrores.direccionSeleccionada = "Selecciona una dirección";
        }

        // Los campos de la dirección son obligatorios en las dos modalidades: incluso
        // viniendo de la ficha del cliente, la API los exige todos y alguna llega coja
        if (!direccion.trim()) {
            nuevosErrores.direccion = "La dirección es requerida";
        }
        if (!codigoPostal.trim()) {
            nuevosErrores.codigoPostal = "El código postal es requerido";
        } else if (!/^\d{5}$/.test(codigoPostal.trim())) {
            nuevosErrores.codigoPostal = "El código postal debe ser 5 dígitos";
        }
        if (!poblacion.trim()) {
            nuevosErrores.poblacion = "La población es requerida";
        }
        if (!provincia.trim()) {
            nuevosErrores.provincia = "La provincia es requerida";
        }

        if (observaciones.length > MAX_OBSERVACIONES) {
            nuevosErrores.observaciones = `Máximo ${MAX_OBSERVACIONES} caracteres`;
        }

        // Longitudes máximas de la BD: sin esto la API responde 400 y el usuario no
        // sabe qué campo se ha pasado. Solo se comprueban si el campo tiene valor,
        // para no tapar el error de "requerido" de arriba.
        const maximos = [
            ['numeroTicket', numeroTicket, MAX_NUMERO_TICKET],
            ['nombreDireccion', nombreDireccion, MAX_DIRECCION_NOMBRE],
            ['direccion', direccion, MAX_DIRECCION],
            ['poblacion', poblacion, MAX_POBLACION],
            ['provincia', provincia, MAX_PROVINCIA],
        ];

        maximos.forEach(([campo, valor, maximo]) => {
            if (valor?.trim() && valor.trim().length > maximo && !nuevosErrores[campo]) {
                nuevosErrores[campo] = `Máximo ${maximo} caracteres`;
            }
        });

        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    // Cambio de modalidad: los datos de una no sirven para la otra, así que se vacían
    const handleTipoClienteChange = (evento, nuevoTipo) => {
        // El grupo es exclusivo y MUI envía null al pulsar el botón ya activo
        if (!nuevoTipo || nuevoTipo === tipoCliente) {
            return;
        }

        setTipoCliente(nuevoTipo);
        setClienteSeleccionado(null);
        setClienteQuery('');
        setDireccionSeleccionada(null);
        setDireccionModificada(false);
        setNombreDireccion('');
        setDireccion('');
        setCodigoPostal('');
        setPoblacion('');
        setProvincia('');
    };

    // Elegir una dirección del cliente: se descarta lo retocado de la anterior para
    // que el efecto vuelva a volcar los campos
    const handleDireccionChange = (linea) => {
        setDireccionModificada(false);
        setDireccionSeleccionada(direccionesOpciones.find((d) => Number(d.linea) === Number(linea)) || null);
    };

    // Escribir en un campo de la dirección. Tocar el nombre de contacto NO la convierte
    // en suelta: completarlo es lo normal cuando la del cliente viene sin nombre.
    const handleCampoDireccionChange = (campo, valor) => {
        const asignar = {
            nombreDireccion: setNombreDireccion,
            direccion: setDireccion,
            codigoPostal: setCodigoPostal,
            poblacion: setPoblacion,
            provincia: setProvincia,
        };

        asignar[campo](valor);

        if (campo !== 'nombreDireccion' && !esClienteFinal) {
            setDireccionModificada(true);
        }
    };

    // Handler para guardar
    const handleGuardar = async () => {
        if (!validar()) {
            return;
        }

        setGuardando(true);
        try {
            const payload = {
                numeroTicket: numeroTicket.trim(),
                // DECISION: la API exige clienteCodigo, y un cliente final no tiene ficha.
                // Se registra contra el cliente contado del ERP, con el nombre escrito
                // como clienteNombre para que el listado no enseñe "CLIENTE CONTADO".
                clienteCodigo: esClienteFinal ? CLIENTE_CONTADO_CODIGO : clienteSeleccionado.codigo,
                clienteNombre: esClienteFinal ? nombreDireccion.trim() : clienteSeleccionado.nombre,
                // Sin línea cuando es cliente final o cuando se ha retocado a mano:
                // la dirección viaja suelta y no apunta a la ficha del cliente
                direccionLinea: (esClienteFinal || direccionModificada || !direccionSeleccionada)
                    ? null
                    : Number(direccionSeleccionada.linea),
                // Campos de dirección por separado
                direccion: {
                    nombre: nombreDireccion.trim() || null,
                    direccion: direccion.trim(),
                    codigoPostal: codigoPostal.trim(),
                    poblacion: poblacion.trim(),
                    provincia: provincia.trim(),
                },
                observaciones: observaciones.trim(),
            };

            await onSave(payload);
        } catch (error) {
            console.error('Error al guardar recogida:', error);
        } finally {
            setGuardando(false);
        }
    };

    // Cabecera compartida por el formulario y por los estados de carga/error, para que la
    // Card no cambie de forma y no haya salto de layout al terminar de cargar
    const cabecera = (
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                    fontWeight: 700,
                    color: "#212529",
                    mb: 0.5,
                }}
            >
                {esEdicion ? "Editar recogida" : "Nueva recogida"}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: "#6c757d",
                }}
            >
                {esEdicion ? "Modifica los datos de la recogida" : "Registra una nueva recogida de mercancía"}
            </Typography>
        </Box>
    );

    const estilosCard = {
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        borderRadius: 2,
    };

    if (cargando || error) {
        return (
            <Box sx={{ width: "100%" }}>
                <Card sx={estilosCard}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        {cabecera}

                        {cargando ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                                <CircularProgress size={40} sx={{ color: "#cc071e" }} />
                            </Box>
                        ) : (
                            <Alert severity="error" sx={{ fontSize: { xs: 13, sm: 14 } }}>
                                {error}
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={estilosCard}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {cabecera}

                    <Stack spacing={{ xs: 2, sm: 3 }}>
                        {/* Motivo del rechazo de MSoft: al guardar sobre una recogida en
                            ERROR el backend reintenta el alta, así que conviene ver por qué
                            falló antes de reintentar */}
                        {esEdicion && recogida?.estado === RECOGIDA_ESTADO.ERROR && recogida?.descripcionErrorMsoft && (
                            <Alert severity="error" sx={{ fontSize: { xs: 12, sm: 13 } }}>
                                <strong>MSoft rechazó esta recogida:</strong> {recogida.descripcionErrorMsoft}
                            </Alert>
                        )}

                        {/* Número de Ticket */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <ConfirmationNumberIcon sx={{ fontSize: 18 }} />
                                Número de Ticket *
                            </Typography>
                            <TextField
                                value={numeroTicket}
                                onChange={(e) => setNumeroTicket(e.target.value)}
                                error={Boolean(errores.numeroTicket)}
                                helperText={errores.numeroTicket}
                                fullWidth
                                placeholder="Ej: TK-12345"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "#fff",
                                    },
                                }}
                            />
                        </Box>

                        {/* Tipo de cliente */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <PersonIcon sx={{ fontSize: 18 }} />
                                Tipo de cliente *
                            </Typography>
                            <ToggleButtonGroup
                                value={tipoCliente}
                                exclusive
                                onChange={handleTipoClienteChange}
                                size="small"
                                aria-label="Tipo de cliente"
                                fullWidth={isMobile}
                                sx={SX_TIPO_CLIENTE}
                            >
                                <ToggleButton value={TIPO_CLIENTE.CLIENTE}>
                                    {TIPO_CLIENTE_LABELS.CLIENTE}
                                </ToggleButton>
                                <ToggleButton value={TIPO_CLIENTE.CLIENTE_FINAL}>
                                    {TIPO_CLIENTE_LABELS.CLIENTE_FINAL}
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Typography
                                variant="caption"
                                sx={{ display: "block", mt: 0.75, color: "#6c757d" }}
                            >
                                {esClienteFinal
                                    ? "Sin ficha en el ERP: escribe a mano la dirección de recogida."
                                    : "Busca el cliente y elige una de las direcciones de su ficha."}
                            </Typography>
                        </Box>

                        {/* Cliente: solo con ficha, el cliente final no tiene */}
                        {!esClienteFinal && (
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                        color: "#495057",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <PersonIcon sx={{ fontSize: 18 }} />
                                    Cliente *
                                </Typography>
                                <Autocomplete
                                    value={clienteSeleccionado}
                                    onChange={(event, newValue) => {
                                        setClienteSeleccionado(newValue);
                                    }}
                                    inputValue={clienteQuery}
                                    onInputChange={(event, newInputValue) => {
                                        setClienteQuery(newInputValue);
                                    }}
                                    options={clientesOpciones}
                                    getOptionLabel={(option) => option.nombre || ""}
                                    isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                                    loading={loadingClientes}
                                    noOptionsText={
                                        clienteQuery.trim().length < BUSQUEDA_MIN_CARACTERES
                                            ? 'Escribe al menos 2 caracteres para buscar'
                                            : 'No se encontraron clientes'
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Buscar cliente..."
                                            error={Boolean(errores.cliente)}
                                            helperText={errores.cliente}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonIcon sx={{ color: "#6c757d" }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <>
                                                        {loadingClientes ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    backgroundColor: "#fff",
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        )}

                        {/* Selector de Dirección */}
                        {!esClienteFinal && (
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                        color: "#495057",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <PlaceIcon sx={{ fontSize: 18 }} />
                                    Dirección *
                                </Typography>
                                <TextField
                                    select
                                    value={direccionSeleccionada?.linea?.toString() || ""}
                                    onChange={(e) => handleDireccionChange(e.target.value)}
                                    disabled={!clienteSeleccionado || loadingDirecciones}
                                    error={Boolean(errores.direccionSeleccionada)}
                                    helperText={
                                        errores.direccionSeleccionada ||
                                        (!clienteSeleccionado ? "Primero selecciona un cliente" : "")
                                    }
                                    fullWidth
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                >
                                    {loadingDirecciones ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Cargando direcciones...
                                        </MenuItem>
                                    ) : [
                                        !clienteSeleccionado && (
                                            <MenuItem key="placeholder" disabled>
                                                Selecciona un cliente primero
                                            </MenuItem>
                                        ),
                                        clienteSeleccionado && direccionesOpciones.length === 0 && (
                                            <MenuItem key="sin-direcciones" disabled>
                                                Este cliente no tiene direcciones: usa "Cliente final"
                                            </MenuItem>
                                        ),
                                        ...direccionesOpciones.map((dir) => (
                                            <MenuItem key={dir.linea} value={dir.linea}>
                                                {dir.direccionCompleta}
                                            </MenuItem>
                                        )),
                                    ]}
                                </TextField>
                            </Box>
                        )}

                        {/* Campos de la dirección */}
                        {(esClienteFinal || direccionSeleccionada) && (
                            <SeccionDireccion
                                valores={{ nombreDireccion, direccion, codigoPostal, poblacion, provincia }}
                                errores={errores}
                                onCampoChange={handleCampoDireccionChange}
                                aviso={
                                    esClienteFinal
                                        ? "Escribe el nombre y la dirección del cliente final donde se recoge la mercancía."
                                        : "Puedes modificar cualquier campo. Los cambios se guardarán en esta recogida, sin tocar la ficha del cliente."
                                }
                            />
                        )}

                        {/* Observaciones */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <NotesIcon sx={{ fontSize: 18 }} />
                                Observaciones
                            </Typography>
                            <TextField
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                                error={Boolean(errores.observaciones)}
                                helperText={
                                    errores.observaciones ||
                                    `${observaciones.length}/${MAX_OBSERVACIONES} caracteres`
                                }
                                multiline
                                rows={4}
                                fullWidth
                                placeholder="Notas adicionales sobre la recogida..."
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "#fff",
                                    },
                                }}
                            />
                        </Box>

                        {/* Botones de acción */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={{ xs: 1, sm: 1.5 }}
                            sx={{ pt: { xs: 1.5, sm: 2 } }}
                        >
                            <Button
                                variant="contained"
                                startIcon={guardando ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />}
                                onClick={handleGuardar}
                                disabled={guardando}
                                fullWidth={isMobile}
                                sx={BOTON_PRIMARIO_SX}
                            >
                                {guardando ? "Guardando..." : "Guardar recogida"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={onCancel}
                                disabled={guardando}
                                fullWidth={isMobile}
                                sx={BOTON_SECUNDARIO_SX}
                            >
                                Cancelar
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}
